import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import '../styles/PerfilAdmin.css';
import { API_URL } from '../config';

type Producto = {
  id: number;
  nombre: string;
  sku: string;
  color: string | null;
  precio: number;
  stock: number;
  low_stock_threshold: number;
};

const API = `${API_URL}/api`;

const PerfilAdmin: React.FC = () => {
  // ======= STATE =======
  const [ordenes, setOrdenes] = useState<any[]>([]);
  const [usuarios, setUsuarios] = useState<any[]>([]);
  const [productos, setProductos] = useState<Producto[]>([]);

  const [filtro, setFiltro] = useState('');
  const [pFiltro, setPFiltro] = useState('');
  const [cargando, setCargando] = useState(true);

  const [isMobile, setIsMobile] = useState<boolean>(
    window.matchMedia('(max-width: 768px)').matches
  );

  // acordeones de secciones en móvil
  const [openSection, setOpenSection] = useState<'ordenes' | 'usuarios' | 'productos' | null>('ordenes');

  // acordeones por-orden en móvil
  const [openOrderIds, setOpenOrderIds] = useState<Record<number, boolean>>({});

  // creación de producto
  const [nuevoProd, setNuevoProd] = useState<Omit<Producto, 'id'>>({
    nombre: '',
    sku: '',
    color: '',
    precio: 0,
    stock: 0,
    low_stock_threshold: 5,
  });

  // ======= TOKEN / CONFIG =======
  const token = localStorage.getItem('access_token');
  const config = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const metodosPago = ['transferencia', 'mercadopago', 'webpay', 'efectivo'];
  const estados = ['completado', 'pendiente', 'en_revision', 'pagada', 'fallida'];

  // ======= MEDIA QUERY LISTENER =======
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const onChange = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  // ======= FETCHERS =======
  const fetchOrdenes = useCallback(async () => {
    try {
      const res = await axios.get(
        `${API}/admin_listar_ordenes/?nro_orden=${encodeURIComponent(filtro)}`,
        config
      );
      setOrdenes(res.data);
    } catch (err) {
      console.error('Error obteniendo ordenes:', err);
    }
  }, [config, filtro]);

  const fetchUsuarios = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin_usuarios/`, config);
      setUsuarios(res.data);
    } catch (err) {
      console.error('Error obteniendo usuarios:', err);
    }
  }, [config]);

  const fetchProductos = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin_productos/?q=${encodeURIComponent(pFiltro)}`, config);
      setProductos(res.data);
    } catch (err) {
      console.error('Error obteniendo productos:', err);
    }
  }, [config, pFiltro]);

  // ======= INIT =======
  useEffect(() => {
    (async () => {
      await Promise.all([fetchOrdenes(), fetchUsuarios(), fetchProductos()]);
      setCargando(false);
    })();
  }, [fetchOrdenes, fetchUsuarios, fetchProductos]);

  // ======= UPDATE HANDLERS =======
  const actualizarUsuario = async (id: number, campo: string, valor: any) => {
    try {
      await axios.put(`${API}/admin_usuarios/${id}/`, { [campo]: valor }, config);
      fetchUsuarios();
    } catch (err) {
      console.error('Error actualizando usuario:', err);
    }
  };

  const actualizarOrden = async (id: number, campo: string, valor: any) => {
    try {
      await axios.put(`${API}/admin_ordenes/${id}/`, { [campo]: valor }, config);
      fetchOrdenes();
    } catch (err) {
      console.error('Error actualizando orden:', err);
    }
  };

  const crearProducto = async () => {
    try {
      if (!nuevoProd.nombre || !nuevoProd.sku) {
        alert('Nombre y SKU son obligatorios.');
        return;
      }
      await axios.post(`${API}/admin_productos/`, nuevoProd, config);
      setNuevoProd({ nombre: '', sku: '', color: '', precio: 0, stock: 0, low_stock_threshold: 5 });
      fetchProductos();
    } catch (e: any) {
      alert('No se pudo crear el producto: ' + (e?.response?.data ? JSON.stringify(e.response.data) : e.message));
    }
  };

  const actualizarProducto = async (id: number, campo: keyof Omit<Producto, 'id'>, valor: any) => {
    try {
      const payload: Partial<Producto> = { [campo]: valor } as any;
      await axios.patch(`${API}/admin_productos/${id}/`, payload, config);
      fetchProductos();
    } catch (err) {
      console.error('Error actualizando producto:', err);
    }
  };

  const eliminarProducto = async (id: number) => {
    if (!window.confirm('¿Eliminar producto?')) return;
    try {
      await axios.delete(`${API}/admin_productos/${id}/`, config);
      setProductos(prev => prev.filter(p => p.id !== id));
    } catch (err) {
      console.error('Error eliminando producto:', err);
    }
  };

  const toggleOrder = (id: number) => {
    setOpenOrderIds(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const labelEstado = (v: string) =>
    v.replace('_', ' ').replace(/\b\w/g, (c) => c.toUpperCase());

  if (cargando) return <div style={{ textAlign: 'center', padding: '100px' }}>Cargando panel...</div>;

  // ======================== RENDER ========================
  return (
    <div className="perfil-admin-container">
      <h2>Panel de Administrador</h2>

      {/* ===================== BUSCADOR ÓRDENES ===================== */}
      <div className="search-section">
        <input
          type="text"
          placeholder="Buscar por nro_orden"
          value={filtro}
          onChange={(e) => setFiltro(e.target.value)}
        />
        <button onClick={fetchOrdenes}>Buscar</button>
      </div>

      {/* ===================== ÓRDENES ===================== */}
      <h3>Órdenes</h3>

{/* Desktop: tabla */}
{!isMobile && (
  <div className="table-wrap">
    <table>
      <thead>
        <tr>
          <th>Nro Orden</th>
          <th>Total</th>
          <th>Método</th>
          <th>Estado</th>
          <th>Usuario ID</th>
          <th>Comprobantes</th>
        </tr>
      </thead>
      <tbody>
        {ordenes.map((orden) => {
          const toArray = (o: any) =>
            Array.isArray(o?.comprobantes)
              ? o.comprobantes
              : o?.comprobante
              ? [o.comprobante]
              : [];

          const files: string[] = toArray(orden);
          const downloads: string[] = Array.isArray(orden?.comprobantes_descarga)
            ? orden.comprobantes_descarga
            : [];

          const isImage = (url: string) =>
            /\.(png|jpe?g|webp|gif|bmp|svg)$/i.test(new URL(url, window.location.origin).pathname);

          return (
            <tr key={orden.id}>
              <td>#{orden.nro_orden}</td>
              <td>
                <input
                  type="number"
                  value={orden.total}
                  onChange={(e) => actualizarOrden(orden.id, 'total', Number(e.target.value))}
                />
              </td>
              <td>
                <select
                  value={orden.metodo_pago}
                  onChange={(e) => actualizarOrden(orden.id, 'metodo_pago', e.target.value)}
                >
                  {metodosPago.map((m) => (
                    <option key={m} value={m}>
                      {m.charAt(0).toUpperCase() + m.slice(1)}
                    </option>
                  ))}
                </select>
              </td>
              <td>
                <select
                  value={(orden.estado || '').trim()}
                  onChange={(e) => actualizarOrden(orden.id, 'estado', e.target.value)}
                >
                  {estados.map((estado) => (
                    <option key={estado} value={estado}>
                      {labelEstado(estado)}
                    </option>
                  ))}
                </select>
              </td>
              <td>{orden.user}</td>

              {/* NUEVO: Comprobantes */}
<td>
  {files.length > 0 ? (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
      {files.map((url: string, i: number) => {
        const viewUrl = url;
        const downloadUrl = downloads[i] || downloads[0] || viewUrl;
        return (

              <a href={downloadUrl} className="btn-secundario" aria-label="Descargar comprobante">
                Descargar
              </a>
     
    
        );
      })}
    </div>
  ) : (
    <span style={{ color: '#6b7280' }}>—</span>
  )}
</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
)}


{/* Mobile: acordeón por orden */}
{isMobile && (
  <div className="mobile-list">
    <button
      className="section-toggle"
      onClick={() => setOpenSection(openSection === 'ordenes' ? null : 'ordenes')}
      aria-expanded={openSection === 'ordenes'}
    >
      Órdenes
      <span className={`chev ${openSection === 'ordenes' ? 'up' : ''}`} />
    </button>

    {openSection === 'ordenes' &&
      ordenes.map((orden) => {
        const abierto = !!openOrderIds[orden.id];

        const toArray = (o: any) =>
          Array.isArray(o?.comprobantes)
            ? o.comprobantes
            : o?.comprobante
            ? [o.comprobante]
            : [];

        const files: string[] = toArray(orden);
        const downloads: string[] = Array.isArray(orden?.comprobantes_descarga)
          ? orden.comprobantes_descarga
          : [];

        const isImage = (u: string) =>
          /\.(png|jpe?g|webp|gif|bmp|svg)$/i.test(new URL(u, window.location.origin).pathname);

        return (
          <div className={`card ${abierto ? 'open' : ''}`} key={orden.id}>
            <button
              className="card-header"
              onClick={() => toggleOrder(orden.id)}
              aria-expanded={abierto}
            >
              <div className="card-title">
                <span className="order-code">#{orden.nro_orden}</span>
                <span className={`badge badge-${(orden.estado || 'pendiente').replace('_', '-')}`}>
                  {labelEstado(orden.estado || 'pendiente')}
                </span>
              </div>
              <div className="chevron" />
            </button>

            {abierto && (
              <div className="card-body">
                <div className="field">
                  <label>Total</label>
                  <input
                    type="number"
                    value={orden.total}
                    onChange={(e) => actualizarOrden(orden.id, 'total', Number(e.target.value))}
                  />
                </div>
                <div className="field">
                  <label>Método</label>
                  <select
                    value={orden.metodo_pago}
                    onChange={(e) => actualizarOrden(orden.id, 'metodo_pago', e.target.value)}
                  >
                    {metodosPago.map((m) => (
                      <option key={m} value={m}>
                        {m.charAt(0).toUpperCase() + m.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="field">
                  <label>Estado</label>
                  <select
                    value={(orden.estado || '').trim()}
                    onChange={(e) => actualizarOrden(orden.id, 'estado', e.target.value)}
                  >
                    {estados.map((estado) => (
                      <option key={estado} value={estado}>
                        {labelEstado(estado)}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="meta">Usuario ID: {orden.user}</div>

                {/* NUEVO: Comprobantes */}
              {/* NUEVO: Comprobantes */}
{files.length > 0 && (
  <div className="field">
    <label>Comprobantes</label>
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
      {files.map((url: string, i: number) => {
        const viewUrl = url;
        const downloadUrl = downloads[i] || downloads[0] || viewUrl;
        return (
          <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          
            <a href={downloadUrl} className="btn-secundario" style={{ marginTop: 4 }} aria-label="Descargar comprobante">
              Descargar
            </a>
          </div>
        );
      })}
    </div>
  </div>
)}

              </div>
            )}
          </div>
        );
      })}
  </div>
)}



      {/* ===================== USUARIOS ===================== */}
      <h3>Usuarios</h3>

      {!isMobile && (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Username</th>
                <th>Email</th>
                <th>Nombre</th>
                <th>Apellido</th>
                <th>Staff</th>
                <th>Activo</th>
                <th>Superuser</th>
              </tr>
            </thead>
            <tbody>
              {usuarios.map((user) => (
                <tr key={user.id}>
                  <td>{user.username}</td>
                  <td>{user.email}</td>
                  <td>{user.first_name}</td>
                  <td>{user.last_name}</td>
                  <td>
                    <input
                      type="checkbox"
                      checked={user.is_staff}
                      onChange={(e) => actualizarUsuario(user.id, 'is_staff', e.target.checked)}
                    />
                  </td>
                  <td>
                    <input
                      type="checkbox"
                      checked={user.is_active}
                      onChange={(e) => actualizarUsuario(user.id, 'is_active', e.target.checked)}
                    />
                  </td>
                  <td>
                    <input
                      type="checkbox"
                      checked={user.is_superuser}
                      onChange={(e) => actualizarUsuario(user.id, 'is_superuser', e.target.checked)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {isMobile && (
        <div className="mobile-list">
          <button
            className="section-toggle"
            onClick={() => setOpenSection(openSection === 'usuarios' ? null : 'usuarios')}
            aria-expanded={openSection === 'usuarios'}
          >
            Usuarios
            <span className={`chev ${openSection === 'usuarios' ? 'up' : ''}`} />
          </button>

          {openSection === 'usuarios' &&
            usuarios.map((u) => (
              <div className="card" key={u.id}>
                <div className="card-header static">
                  <div className="card-title">
                    <span className="order-code">{u.username}</span>
                    <span className="badge">{u.email}</span>
                  </div>
                </div>
                <div className="card-body">
                  <div className="meta">Nombre: {u.first_name} {u.last_name}</div>
                  <div className="toggles">
                    <label><input type="checkbox" checked={u.is_staff} onChange={(e) => actualizarUsuario(u.id, 'is_staff', e.target.checked)} /> Staff</label>
                    <label><input type="checkbox" checked={u.is_active} onChange={(e) => actualizarUsuario(u.id, 'is_active', e.target.checked)} /> Activo</label>
                    <label><input type="checkbox" checked={u.is_superuser} onChange={(e) => actualizarUsuario(u.id, 'is_superuser', e.target.checked)} /> Superuser</label>
                  </div>
                </div>
              </div>
            ))}
        </div>
      )}

      {/* ===================== PRODUCTOS ===================== */}
      <h3>Productos</h3>

      <div className="search-section">
        <input
          type="text"
          placeholder="Buscar por nombre / SKU / color"
          value={pFiltro}
          onChange={(e) => setPFiltro(e.target.value)}
        />
        <button onClick={fetchProductos}>Buscar</button>
      </div>

      {/* Desktop: tabla */}
      {!isMobile && (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Nombre</th>
                <th>SKU</th>
                <th>Color</th>
                <th>Precio</th>
                <th>Stock</th>
                <th>Umbral</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {/* creación */}
              <tr>
                <td><input value={nuevoProd.nombre} onChange={e => setNuevoProd(p => ({ ...p, nombre: e.target.value }))} /></td>
                <td><input value={nuevoProd.sku} onChange={e => setNuevoProd(p => ({ ...p, sku: e.target.value }))} /></td>
                <td><input value={nuevoProd.color ?? ''} onChange={e => setNuevoProd(p => ({ ...p, color: e.target.value }))} /></td>
                <td><input type="number" value={nuevoProd.precio} onChange={e => setNuevoProd(p => ({ ...p, precio: Number(e.target.value || 0) }))} /></td>
                <td><input type="number" value={nuevoProd.stock} onChange={e => setNuevoProd(p => ({ ...p, stock: Number(e.target.value || 0) }))} /></td>
                <td><input type="number" value={nuevoProd.low_stock_threshold} onChange={e => setNuevoProd(p => ({ ...p, low_stock_threshold: Number(e.target.value || 0) }))} /></td>
                <td><button onClick={crearProducto}>Crear</button></td>
              </tr>

              {/* existentes */}
              {productos.map((p) => {
                const low = p.stock <= p.low_stock_threshold;
                return (
                  <tr key={p.id} className={low ? 'row-low-stock' : ''}>
                    <td><input value={p.nombre} onChange={(e) => actualizarProducto(p.id, 'nombre', e.target.value)} /></td>
                    <td><input value={p.sku} onChange={(e) => actualizarProducto(p.id, 'sku', e.target.value)} /></td>
                    <td><input value={p.color || ''} onChange={(e) => actualizarProducto(p.id, 'color', e.target.value)} /></td>
                    <td><input type="number" value={p.precio} onChange={(e) => actualizarProducto(p.id, 'precio', Number(e.target.value || 0))} /></td>
                    <td><input type="number" value={p.stock} onChange={(e) => actualizarProducto(p.id, 'stock', Number(e.target.value || 0))} /></td>
                    <td><input type="number" value={p.low_stock_threshold} onChange={(e) => actualizarProducto(p.id, 'low_stock_threshold', Number(e.target.value || 0))} /></td>
                    <td><button className="danger" onClick={() => eliminarProducto(p.id)}>Eliminar</button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Mobile: acordeón + cards */}
      {isMobile && (
        <div className="mobile-list">
          <button
            className="section-toggle"
            onClick={() => setOpenSection(openSection === 'productos' ? null : 'productos')}
            aria-expanded={openSection === 'productos'}
          >
            Productos
            <span className={`chev ${openSection === 'productos' ? 'up' : ''}`} />
          </button>

          {openSection === 'productos' && (
            <>
              {/* Card de creación */}
              <div className="card">
                <div className="card-header static">
                  <div className="card-title">Nuevo Producto</div>
                </div>
                <div className="card-body">
                  <div className="field"><label>Nombre</label><input value={nuevoProd.nombre} onChange={e => setNuevoProd(p => ({ ...p, nombre: e.target.value }))} /></div>
                  <div className="field"><label>SKU</label><input value={nuevoProd.sku} onChange={e => setNuevoProd(p => ({ ...p, sku: e.target.value }))} /></div>
                  <div className="field"><label>Color</label><input value={nuevoProd.color ?? ''} onChange={e => setNuevoProd(p => ({ ...p, color: e.target.value }))} /></div>
                  <div className="field"><label>Precio</label><input type="number" value={nuevoProd.precio} onChange={e => setNuevoProd(p => ({ ...p, precio: Number(e.target.value || 0) }))} /></div>
                  <div className="field"><label>Stock</label><input type="number" value={nuevoProd.stock} onChange={e => setNuevoProd(p => ({ ...p, stock: Number(e.target.value || 0) }))} /></div>
                  <div className="field"><label>Umbral</label><input type="number" value={nuevoProd.low_stock_threshold} onChange={e => setNuevoProd(p => ({ ...p, low_stock_threshold: Number(e.target.value || 0) }))} /></div>
                  <button onClick={crearProducto}>Crear</button>
                </div>
              </div>

              {/* Cards existentes */}
              {productos.map((p) => {
                const low = p.stock <= p.low_stock_threshold;
                return (
                  <div className={`card ${low ? 'low' : ''}`} key={p.id}>
                    <div className="card-header static">
                      <div className="card-title">
                        <span className="order-code">{p.sku}</span>
                        <span className="badge">{p.nombre}{p.color ? ` (${p.color})` : ''}</span>
                      </div>
                    </div>
                    <div className="card-body">
                      <div className="field"><label>Nombre</label><input value={p.nombre} onChange={(e)=>actualizarProducto(p.id,'nombre', e.target.value)} /></div>
                      <div className="field"><label>Color</label><input value={p.color || ''} onChange={(e)=>actualizarProducto(p.id,'color', e.target.value)} /></div>
                      <div className="field"><label>Precio</label><input type="number" value={p.precio} onChange={(e)=>actualizarProducto(p.id,'precio', Number(e.target.value || 0))} /></div>
                      <div className="field"><label>Stock</label><input type="number" value={p.stock} onChange={(e)=>actualizarProducto(p.id,'stock', Number(e.target.value || 0))} /></div>
                      <div className="field"><label>Umbral</label><input type="number" value={p.low_stock_threshold} onChange={(e)=>actualizarProducto(p.id,'low_stock_threshold', Number(e.target.value || 0))} /></div>
                      <button className="danger" onClick={()=>eliminarProducto(p.id)}>Eliminar</button>
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default PerfilAdmin;
